(function () {
  // Sparks Tire Manager — drive as far as possible before your worst tire's
  // wear hits 100%. Fixed HUD, home page only (see theme.liquid), gated by
  // the "Enable Games" flag (window.Car77Games, set up in racing-theme.js).
  // Self-contained: only touches window.Car77Games.isEnabled() from outside.
  var root = document.querySelector('[data-tire-game]');
  if (!root) return;

  var TIRE_KEYS = ['FL', 'FR', 'RL', 'RR'];
  // Tires now START in the "cool" (light blue) zone, not green — the player
  // has to actually warm up to reach the optimal window, rather than
  // starting there for free.
  var START_TEMP = 50;
  var MIN_TEMP = 20;
  var MAX_TEMP = 140;
  // %/sec at a 1.0x wear multiplier.
  var BASE_WEAR_RATE = 0.6;
  // First quarter mile: no wear at all, AND temperature can't actually
  // reach the extreme cold/hot buckets — it approaches SOFT_MIN/SOFT_MAX
  // exponentially (asymptotic — gets arbitrarily close, never crosses) so
  // there's real room to learn the controls without an instant, unlucky
  // trip into "cold" or "critical" before you've even got a feel for it.
  // Both mechanics release together at the same distance.
  var ONBOARDING_DISTANCE = 0.25;
  var SOFT_MIN = 40;
  var SOFT_MAX = 105;
  var SOFT_APPROACH_RATE = 1.5;
  var BEST_KEY = 'car77-tire-game-best';

  // Green (optimal) is the untouched reference point: fast AND safe, the
  // skill-based ideal. Every other bucket now pulls harder away from it in
  // BOTH speed and wear (not just wear) so the buckets on either side read
  // as two genuinely different, roughly-as-viable playstyles rather than
  // "green, and everything else is just worse": cool trades speed for very
  // low wear (conservative — outlast everyone), hot trades wear for a real
  // speed boost above even green (aggressive — burn through tires for
  // bursts of speed). Cold and critical stay clear mistake zones — both are
  // tuned to go from 20% to 100% wear in about 10 seconds sustained, a real
  // "get out now" punishment, not a slow drain.
  var BUCKETS = {
    cold: { max: 39, speed: 40, wear: 13.3 },
    cool: { max: 59, speed: 80, wear: 0.7 },
    optimal: { max: 85, speed: 180, wear: 0.4 },
    hot: { max: 105, speed: 250, wear: 2.5 },
    critical: { max: Infinity, speed: 45, wear: 13.3 }
  };
  var BUCKET_ORDER = ['cold', 'cool', 'optimal', 'hot', 'critical'];
  // Text (not the card background) shakes once things get genuinely
  // dangerous — overheating (critical) or the rare cold-tearing case — not
  // for merely "cool".
  var DANGEROUS_BUCKETS = { cold: true, critical: true };
  // Panel glow: if ANY tire is hot (hot or critical bucket) it glows red on
  // the outside; else if ANY tire is freezing (cold bucket) it glows a
  // strong blue on the inside; otherwise it's just the default subtle white
  // glow. Hot takes priority in the rare case both are true at once, since
  // overheating burns through wear much faster (see BUCKETS.critical.wear).
  var GLOW_DEFAULT = 'rgba(255, 255, 255, 0.35)';
  var GLOW_HOT = 'rgba(255, 30, 39, 0.7)';
  var GLOW_FREEZING = 'rgba(30, 110, 255, 0.8)';
  var HOT_BUCKETS = { hot: true, critical: true };
  var FREEZING_BUCKETS = { cold: true };

  // Base per-tire °C/sec deltas (before the ramp-up-from-a-standstill rate
  // multiplier scales them). Brake overrides everything else (dedicated
  // emergency cool). Otherwise: the core idea is that whichever axle ISN'T
  // doing the work cools down, harder than passive coasting ever does (idle
  // is deliberately minimal — driving the opposite axle is the real way to
  // bring a tire down). Turning alone splits the rear cooling: the outside
  // rear (not on the turn's side) cools more than the inside rear, mirroring
  // the inside-front-heats-faster split. But holding gas WHILE turning means
  // the rears are still being driven — they heat instead of cooling, just
  // at 75% of the normal straight-line rate, rather than getting the
  // turning-alone cooling treatment.
  var STRAIGHT_REAR_HEAT = 15;
  var TURN_GAS_REAR_HEAT = STRAIGHT_REAR_HEAT * 0.75;

  function getDeltas(input) {
    if (input.down) {
      return { FL: -18, FR: -18, RL: -18, RR: -18 };
    }

    if (input.left || input.right) {
      var d = input.left ? { FL: 18, FR: 12 } : { FL: 12, FR: 18 };

      if (input.up) {
        d.RL = TURN_GAS_REAR_HEAT;
        d.RR = TURN_GAS_REAR_HEAT;
      } else if (input.left) {
        // Turning left: outside = right side (FR/RR).
        d.RL = -5;
        d.RR = -10;
      } else {
        // Turning right: outside = left side (FL/RL).
        d.RL = -10;
        d.RR = -5;
      }

      return d;
    }

    if (input.up) {
      return { FL: -10, FR: -10, RL: STRAIGHT_REAR_HEAT, RR: STRAIGHT_REAR_HEAT };
    }

    // Coasting/no input: still minimal relative to actually driving the
    // opposite axle, but 25% quicker than before (-2 -> -2.5) per feedback.
    return { FL: -2.5, FR: -2.5, RL: -2.5, RR: -2.5 };
  }

  // Heat/cool rate ramps up from a standstill rather than escalating
  // forever: half rate for the first half mile, three-quarter rate through
  // mile 1, full (normal) rate for good after that. Applies to heating AND
  // cooling alike.
  function getRateMultiplier(distanceSoFar) {
    if (distanceSoFar < 0.5) return 0.5;
    if (distanceSoFar < 1) return 0.75;
    return 1;
  }

  // Separate, heating-only penalty stacked on top of the above: past the
  // half-mile mark, warming up gets permanently 25% harder (cooling is
  // unaffected) — the longer the run goes, the more it rewards staying
  // disciplined rather than just gunning it back into the hot zone.
  var HARDER_WARMUP_DISTANCE = 0.5;
  var HARDER_WARMUP_MULTIPLIER = 0.75;

  var KEY_MAP = {
    KeyW: 'up',
    ArrowUp: 'up',
    KeyS: 'down',
    ArrowDown: 'down',
    KeyA: 'left',
    ArrowLeft: 'left',
    KeyD: 'right',
    ArrowRight: 'right'
  };

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function getBucket(temp) {
    for (var i = 0; i < BUCKET_ORDER.length; i++) {
      var name = BUCKET_ORDER[i];
      if (temp <= BUCKETS[name].max) return name;
    }
    return 'critical';
  }

  // --- DOM refs ---
  var helpBtn = root.querySelector('[data-tire-game-help-btn]');
  var helpTooltip = root.querySelector('[data-tire-game-help-tooltip]');
  var speedEl = root.querySelector('[data-tire-game-speed]');
  var wearValueEl = root.querySelector('[data-tire-game-wear-value]');
  var wearSegments = root.querySelectorAll('[data-tire-game-wear-segment]');
  var distanceEl = root.querySelector('[data-tire-game-distance]');
  var bestEl = root.querySelector('[data-tire-game-best]');
  var gameOverDistanceEl = root.querySelector('[data-tire-game-final-distance]');
  var gameOverBestEl = root.querySelector('[data-tire-game-gameover-best]');
  var restartBtn = root.querySelector('[data-tire-game-restart]');
  var startBtn = root.querySelector('[data-tire-game-start]');
  var howToPlayBtn = root.querySelector('[data-tire-game-how-to-play]');
  // Siblings of root, not descendants — see the comment in tire-game.liquid
  // for why the rules drawer lives outside .tire-game in the markup.
  var rulesPanel = document.querySelector('[data-tire-game-rules]');
  var rulesCloseBtn = document.querySelector('[data-tire-game-rules-close]');
  var dpadButtons = root.querySelectorAll('[data-dpad]');

  var tireEls = {};
  TIRE_KEYS.forEach(function (key) {
    tireEls[key] = {
      card: root.querySelector('[data-tire="' + key + '"]'),
      label: root.querySelector('[data-tire="' + key + '"] .tire-game__tire-label'),
      temp: root.querySelector('[data-tire="' + key + '"] [data-tire-temp]')
    };
  });

  // --- Game state ---
  var tires;
  var distance;
  var input = { up: false, down: false, left: false, right: false };
  var running = false;
  var rafId = null;
  var lastTime = 0;

  function freshTires() {
    var t = {};
    TIRE_KEYS.forEach(function (key) {
      t[key] = { temp: START_TEMP, wear: 0 };
    });
    return t;
  }

  function resetState() {
    tires = freshTires();
    distance = 0;
    root.classList.remove('is-game-over');
    render(0, 0);
  }

  function render(maxWear, carSpeedMph) {
    var anyHot = false;
    var anyFreezing = false;

    TIRE_KEYS.forEach(function (key) {
      var tire = tires[key];
      var bucket = getBucket(tire.temp);
      var els = tireEls[key];
      var dangerous = !!DANGEROUS_BUCKETS[bucket];
      if (HOT_BUCKETS[bucket]) anyHot = true;
      if (FREEZING_BUCKETS[bucket]) anyFreezing = true;
      if (els.card) els.card.setAttribute('data-bucket', bucket);
      if (els.label) els.label.classList.toggle('tire-game__tire-label--shake', dangerous);
      if (els.temp) {
        els.temp.textContent = Math.round(tire.temp) + '°C';
        els.temp.classList.toggle('tire-game__tire-temp--shake', dangerous);
      }
    });

    if (anyHot) {
      root.style.setProperty('--glow-color', GLOW_HOT);
      root.classList.remove('tire-game--glow-inside');
    } else if (anyFreezing) {
      root.style.setProperty('--glow-color', GLOW_FREEZING);
      root.classList.add('tire-game--glow-inside');
    } else {
      root.style.setProperty('--glow-color', GLOW_DEFAULT);
      root.classList.remove('tire-game--glow-inside');
    }

    if (speedEl) speedEl.textContent = Math.round(carSpeedMph) + ' MPH';

    if (wearValueEl) wearValueEl.textContent = maxWear.toFixed(1) + '%';
    var filledSegments = Math.round((maxWear / 100) * wearSegments.length);
    wearSegments.forEach(function (segment, i) {
      segment.classList.toggle('is-filled', i < filledSegments);
    });

    if (distanceEl) distanceEl.textContent = distance.toFixed(2) + ' mi';
  }

  function getBest() {
    return parseFloat(localStorage.getItem(BEST_KEY) || '0');
  }

  function updateBestDisplay() {
    if (bestEl) bestEl.textContent = 'Best: ' + getBest().toFixed(2) + ' mi';
  }

  function endGame() {
    stopLoop();
    root.classList.add('is-game-over');

    var best = getBest();
    if (distance > best) {
      localStorage.setItem(BEST_KEY, distance.toFixed(2));
      best = distance;
    }
    updateBestDisplay();

    if (gameOverDistanceEl) gameOverDistanceEl.textContent = distance.toFixed(2) + ' mi';
    if (gameOverBestEl) gameOverBestEl.textContent = 'High Score: ' + best.toFixed(2) + ' mi';
  }

  function tick(now) {
    if (!running) return;

    var dt = Math.min((now - lastTime) / 1000, 0.1);
    lastTime = now;

    var deltas = getDeltas(input);
    var rate = getRateMultiplier(distance);
    var warmupPenalty = distance >= HARDER_WARMUP_DISTANCE ? HARDER_WARMUP_MULTIPLIER : 1;
    var inOnboarding = distance < ONBOARDING_DISTANCE;
    var speeds = [];
    var maxWear = 0;

    TIRE_KEYS.forEach(function (key) {
      var tire = tires[key];
      var rawDelta = deltas[key];
      var effectiveDelta = rawDelta > 0 ? rawDelta * warmupPenalty : rawDelta;
      var newTemp = tire.temp + effectiveDelta * rate * dt;

      if (inOnboarding) {
        if (newTemp > SOFT_MAX) {
          var gapHigh = Math.max(SOFT_MAX - tire.temp, 0);
          newTemp = SOFT_MAX - gapHigh * Math.exp(-SOFT_APPROACH_RATE * dt);
        } else if (newTemp < SOFT_MIN) {
          var gapLow = Math.max(tire.temp - SOFT_MIN, 0);
          newTemp = SOFT_MIN + gapLow * Math.exp(-SOFT_APPROACH_RATE * dt);
        }
      }

      tire.temp = clamp(newTemp, MIN_TEMP, MAX_TEMP);
      var bucketInfo = BUCKETS[getBucket(tire.temp)];
      speeds.push(bucketInfo.speed);
      if (!inOnboarding) {
        tire.wear = clamp(tire.wear + BASE_WEAR_RATE * bucketInfo.wear * dt, 0, 100);
      }
      maxWear = Math.max(maxWear, tire.wear);
    });

    var carSpeedMph = Math.min.apply(null, speeds);
    distance += carSpeedMph * (dt / 3600);

    render(maxWear, carSpeedMph);

    if (maxWear >= 100) {
      endGame();
      return;
    }

    rafId = window.requestAnimationFrame(tick);
  }

  function startLoop() {
    if (running) return;
    running = true;
    lastTime = performance.now();
    rafId = window.requestAnimationFrame(tick);
  }

  function stopLoop() {
    running = false;
    if (rafId) window.cancelAnimationFrame(rafId);
    rafId = null;
  }

  // --- Input: keyboard (ignored while typing into a real form field) ---
  document.addEventListener('keydown', function (e) {
    if (!root.classList.contains('is-playing')) return;
    var target = e.target;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    var dir = KEY_MAP[e.code];
    if (!dir) return;
    input[dir] = true;
  });

  document.addEventListener('keyup', function (e) {
    var dir = KEY_MAP[e.code];
    if (!dir) return;
    input[dir] = false;
  });

  // --- Input: mobile D-pad (shared input state with keyboard) ---
  dpadButtons.forEach(function (btn) {
    var dir = btn.getAttribute('data-dpad');

    ['touchstart', 'mousedown'].forEach(function (evt) {
      btn.addEventListener(
        evt,
        function (e) {
          e.preventDefault();
          input[dir] = true;
          btn.classList.add('is-active');
        },
        { passive: false }
      );
    });

    ['touchend', 'touchcancel', 'mouseup', 'mouseleave'].forEach(function (evt) {
      btn.addEventListener(evt, function () {
        input[dir] = false;
        btn.classList.remove('is-active');
      });
    });
  });

  // --- Help tooltip (quick controls reminder, available during play) ---
  if (helpBtn && helpTooltip) {
    helpBtn.addEventListener('click', function () {
      var expanded = helpBtn.classList.toggle('is-active');
      helpTooltip.hidden = !expanded;
    });

    document.addEventListener('click', function (e) {
      if (helpBtn.contains(e.target) || helpTooltip.contains(e.target)) return;
      helpBtn.classList.remove('is-active');
      helpTooltip.hidden = true;
    });
  }

  // --- "How to Play" — a large standalone popup that slides out like a
  // drawer, opened from the start screen's button, closable via its own ×
  // or by clicking outside it. Uses an .is-open class rather than the
  // hidden attribute so the CSS transition (opacity/transform) can actually
  // animate — [hidden] maps to display:none, which can't be transitioned. */
  if (howToPlayBtn && rulesPanel) {
    howToPlayBtn.addEventListener('click', function () {
      rulesPanel.classList.toggle('is-open');
    });

    if (rulesCloseBtn) {
      rulesCloseBtn.addEventListener('click', function () {
        rulesPanel.classList.remove('is-open');
      });
    }

    document.addEventListener('click', function (e) {
      if (!rulesPanel.classList.contains('is-open')) return;
      if (rulesPanel.contains(e.target) || howToPlayBtn.contains(e.target)) return;
      rulesPanel.classList.remove('is-open');
    });
  }

  // --- Start / Restart: the start screen only ever shows once per page
  // load, before the first "Start Driving" click. From then on, showing
  // the widget again (settings toggle) resumes the run in progress, and a
  // finished run's "Play Again" goes straight back into driving. ---
  function beginRun() {
    root.classList.add('is-playing');
    resetState();
    startLoop();
  }

  if (startBtn) {
    startBtn.addEventListener('click', beginRun);
  }

  if (restartBtn) {
    restartBtn.addEventListener('click', beginRun);
  }

  // --- Show/hide tied to the "Enable Games" flag, live (no reload needed) ---
  function updateVisibility() {
    var enabled = window.Car77Games && window.Car77Games.isEnabled();
    root.classList.toggle('is-visible', !!enabled);

    if (!enabled && rulesPanel) {
      // Independent of root now (see the drawer comment above) — close it
      // explicitly rather than relying on root's own display:none.
      rulesPanel.classList.remove('is-open');
    }

    if (enabled && root.classList.contains('is-playing') && !root.classList.contains('is-game-over')) {
      startLoop();
    } else {
      stopLoop();
    }
  }

  window.addEventListener('car77:games-changed', updateVisibility);

  resetState();
  updateBestDisplay();
  updateVisibility();
})();
